import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import ScopesStatusStyle from '../css/ScopesStatus.module.css'
import { API_DEFAULT } from "../services/settings";
import { API } from "../services/routes";
import { request, tokenExpired, hasCode } from "../services/http";

// The cross is offered on a job still running that the signed-in user may
// stop: its owner, or anyone of CNC (back-ratings #102), as the back says in
// can_cancel since its #107. A process without the field keeps the cross, so
// an older back still shows it and its 403 is explained.
const mayCancel = (scope) => parseFloat(scope.avance) < 100 && scope.can_cancel !== false;

const ScopesStatus = forwardRef((props, ref) => {
    const { setAccessToken, accessToken, getAccessTokenWithRefresh, setShowLoader, setMessage, setSeverity, setShowAlert } = props
    const [scopesProcess, setScopesProcess] = useState([])

    // The queue of the scopes and reach-neto jobs launched in the last 24
    // hours, newest first. It is asked for when the portal loads, when the
    // panel opens and on its "Actualizar" (decided 2026-09-22): not on every
    // refresh of the access token, which used to fetch it twice every five
    // minutes (once from here and once from Visor.jsx).
    const getScopesProcess = async (access = accessToken, afterRefresh = false) => {
        setShowLoader(true)
        const answer = await request(
            `${API_DEFAULT}/${API.processes}`, {
            headers: {
                "Authorization": "Bearer " + access
            }
        })

        if (tokenExpired(answer) && !afterRefresh) {
            // Asked again with the fresh token, once; the effect that used to
            // do it by rerunning on every token change is gone.
            let newAccess = await getAccessTokenWithRefresh();
            if (newAccess) {
                setAccessToken(newAccess);
                localStorage.setItem('accessToken', newAccess);
                return getScopesProcess(newAccess, true);
            }
            setShowLoader(false)
            return
        }

        if (!answer.ok || !Array.isArray(answer.data)) {
            setShowLoader(false)
            setMessage("No fue posible consultar el estado de las consultas. Intente de nuevo.")
            setSeverity("danger")
            setTimeout(() => { setShowAlert(true) }, 0)
            return
        }

        let sortedData = [...answer.data].sort((a, b) => {
            if (a.fecha_creado > b.fecha_creado) {
                return -1;
            }
            if (a.fecha_creado < b.fecha_creado) {
                return 1;
            }
            return 0;
        })

        const now = new Date()
        // The queue shows a day's work: anything older is dropped.
        const filterOldObjects = (arr) => {
            return arr.filter(item => {
                const createdDate = new Date(item.fecha_creado)
                const hoursDiff = (now - createdDate) / (1000 * 60 * 60)
                return hoursDiff <= 24;
            });
        };

        setScopesProcess(filterOldObjects(sortedData))
        setShowLoader(false)
    }

    const handleCancel = async (id, access = accessToken) => {
        setShowLoader(true)

        let body = new FormData();
        body.append("internal_id", id)

        const answer = await request(
            `${API_DEFAULT}/${API.killProcess}`, {
            method: 'POST',
            headers: {
                "Authorization": "Bearer " + access
            },
            body: body
        }
        )

        if (tokenExpired(answer)) {
            let newAccess = await getAccessTokenWithRefresh();
            if (newAccess) {
                setAccessToken(newAccess);
                localStorage.setItem('accessToken', newAccess);

                handleCancel(id, newAccess);
            }
        }
        else if (answer.ok && hasCode(answer, 'process_killed')) {
            setShowLoader(false);
            // With the token it cancelled with; it was called with none, sent
            // "Bearer undefined" and forced a refresh.
            getScopesProcess(access);
            setMessage("La consulta ha sido cancelada.");
            setSeverity("info");
            setTimeout(() => { setShowAlert(true) }, 0);
        }
        // Since back-ratings #102 only the process's owner, or CNC, may stop
        // it, while the queue still lists the organisation's every process.
        else if (hasCode(answer, 'process_not_yours')) {
            setShowLoader(false);
            setMessage("Solo quien lanzó la consulta, o CNC, puede cancelarla.");
            setSeverity("danger");
            setTimeout(() => { setShowAlert(true) }, 0);
        }
        else {
            setShowLoader(false);
            setMessage("Error");
            setSeverity("danger");
            setTimeout(() => { setShowAlert(true) }, 0);
        }
    }

    // Once, when the portal loads.
    useEffect(() => {
        getScopesProcess(accessToken)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useImperativeHandle(ref, () => ({
        getScopesProcess
    }));

    const process = scopesProcess.length > 0 ? scopesProcess.map((scope) => {
        return (
            <div className={ScopesStatusStyle["progress-item"]}>
                <div className={ScopesStatusStyle["progress-info"]}>
                    <span className={ScopesStatusStyle["username"]}>{'@' + scope.username.split('@')[0]}</span>
                    <span className={ScopesStatusStyle["filename"]}>{scope.nombre}</span>
                </div>
                <div className={ScopesStatusStyle["progress-bar"]}>
                    {scope.estado === 2 ? (
                        <div className={ScopesStatusStyle["progress"]} style={{ width: '100%', backgroundColor: 'transparent', 'color': '#b91717' }}>☓ Error generando consulta</div>
                    ) : scope.estado === 3 ? (
                        // <div className={ScopesStatusStyle["progress"]} style={{ width: '100%', backgroundColor: 'transparent', 'color': '#346e9b' }}>ⓘ Consulta en cola de procesos</div>
                        <div style={{ position: 'relative', display: 'inline-block', width: "100%" }}>
                            <div
                                className={ScopesStatusStyle["progress"]}
                                style={{
                                    width: '100%',
                                    backgroundColor: 'transparent',
                                    'color': '#346e9b'
                                }}
                            >
                                ⓘ Consulta en cola de procesos
                            </div>
                            {mayCancel(scope) && (
                                <button
                                    onClick={() => handleCancel(scope.id)}
                                    style={{
                                        position: 'absolute',
                                        top: '0',
                                        right: '4px',
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#424242',
                                        fontWeight: 'bold',
                                        fontSize: '16px',
                                        cursor: 'pointer',
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'background 0.3s ease, color 0.3s ease',
                                    }}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.background = '#f5cccc';
                                        e.currentTarget.style.color = '#b91717';
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.background = 'transparent';
                                        e.currentTarget.style.color = '#424242';
                                    }}
                                    aria-label="Cancelar"
                                    title="Cancelar consulta"
                                >
                                    ×
                                </button>

                            )}
                        </div>
                    ) : scope.estado === 4 ? (
                        <div className={ScopesStatusStyle["progress"]} style={{ width: '100%', backgroundColor: 'transparent', 'color': '#424242' }}>ⓘ Consulta cancelada por el usuario</div>
                    ) : (
                        // <div className={ScopesStatusStyle["progress"]} style={{ width: `${scope.avance}%`, backgroundColor: `${Number(scope.avance) !== 0 ? '#64a454' : 'transparent'}` }}>{scope.avance}%</div>

                        <div style={{ position: 'relative', display: 'inline-block', width: "100%" }}>
                            <div
                                className={ScopesStatusStyle["progress"]}
                                style={{
                                    width: `${scope.avance}%`,
                                    backgroundColor: `${Number(scope.avance) !== 0 ? '#64a454' : 'transparent'}`
                                }}
                            >
                                {scope.avance}%
                            </div>
                            {mayCancel(scope) && (
                                <button
                                    onClick={() => handleCancel(scope.id)}
                                    style={{
                                        position: 'absolute',
                                        top: '0',
                                        right: '4px',
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#424242',
                                        fontWeight: 'bold',
                                        fontSize: '16px',
                                        cursor: 'pointer',
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'background 0.3s ease, color 0.3s ease',
                                    }}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.background = '#f5cccc';
                                        e.currentTarget.style.color = '#b91717';
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.background = 'transparent';
                                        e.currentTarget.style.color = '#424242';
                                    }}
                                    aria-label="Cancelar"
                                    title="Cancelar consulta"
                                >
                                    ×
                                </button>

                            )}
                        </div>

                    )}
                </div>
            </div>
        );
    }) : <div className={ScopesStatusStyle["no-scopes-process"]}>No hay alcances para mostrar</div>;

    const hideScopesStatus = () => {
        const scopesStatus = document.getElementById('scopes-status')
        scopesStatus.classList.remove(ScopesStatusStyle["hide-scopes-status"])
        scopesStatus.classList.toggle(ScopesStatusStyle["show-scopes-status"])
    }

    return (
        <>
            <div className={ScopesStatusStyle["scopes-status"]} id="scopes-status">
                <div className={ScopesStatusStyle["layer-hide-menu"]} onClick={() => hideScopesStatus()}></div>
                <div className={ScopesStatusStyle["scopes-status-container"]} id="scopes-status-container">
                    <div className={ScopesStatusStyle["scopes-status-header"]}>
                        <h2>Estado de consultas</h2>
                    </div>

                    <div className={ScopesStatusStyle["scopes-status-container-scroll"]}>
                        <div className={ScopesStatusStyle["progress-container"]}>
                            {process}
                        </div>
                    </div>

                    <div className={ScopesStatusStyle["scopes-status-button-container"]}>
                        <div className={ScopesStatusStyle["update-status"]} onClick={() => getScopesProcess(accessToken)}>
                            <ion-icon name="reload-outline"></ion-icon>
                            <span>Actualizar</span>
                        </div>
                        <button className={ScopesStatusStyle["scopes-status-button-cancel"]} onClick={() => hideScopesStatus()}>CERRAR</button>
                    </div>

                </div>
            </div>
        </>
    );
});

export default ScopesStatus;
